import React from 'react';
import { Box, Typography, Chip, Skeleton, Tooltip, IconButton } from '@mui/material';
import { Refresh as RefreshIcon, MonetizationOn as MonetizationOnIcon } from '@mui/icons-material';
import { useMoaniPrice } from '../contexts/MoaniPriceContext';

/**
 * MOANI Price Display for Header/Navbar
 * Shows current MOANI price with refresh functionality
 */
function MoaniPriceHeader({ compact = false }) {
  const { price, loading, lastUpdated, refreshPrice } = useMoaniPrice();

  const handleRefresh = async () => {
    try {
      await refreshPrice();
    } catch (error) {
      console.warn('Failed to refresh MOANI price:', error);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '$0.0025';
    
    // For very small prices (like MOANI), show with appropriate decimals
    // For larger prices, show without decimals
    if (price < 0.01) {
      return `$${price.toFixed(4)}`;
    } else if (price < 1) {
      return `$${price.toFixed(2)}`;
    } else {
      return price.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        style: 'currency',
        currency: 'USD'
      });
    }
  };

  const getTimeSinceUpdate = () => {
    if (!lastUpdated) return 'Never updated';
    
    const now = new Date();
    const diffMs = now - lastUpdated;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just updated';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  };

  const tooltipContent = (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        MOANI Token Price
      </Typography>
      <Typography variant="body2">
        Current: {formatPrice(price)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Last updated: {getTimeSinceUpdate()}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Click refresh to update price
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 0.5,
          cursor: 'help'
        }}
      >
        <MonetizationOnIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
        
        {loading && !price ? (
          <Skeleton width={compact ? 50 : 60} height={24} />
        ) : (
          <Chip
            label={compact ? formatPrice(price) : `MOANI ${formatPrice(price)}`}
            size="small"
            variant="outlined"
            sx={{
              fontSize: compact ? '0.7rem' : '0.75rem',
              height: 24,
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              backgroundColor: 'rgba(0, 229, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(0, 229, 255, 0.2)',
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '& .MuiChip-label': {
                px: compact ? 0.5 : 1,
              },
            }}
          />
        )}
        
        <IconButton
          size="small"
          onClick={handleRefresh}
          disabled={loading}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            width: 20,
            height: 20,
            '&:hover': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&:disabled': {
              color: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          <RefreshIcon 
            sx={{ 
              fontSize: '0.9rem',
              animation: loading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': {
                  transform: 'rotate(0deg)',
                },
                '100%': {
                  transform: 'rotate(360deg)',
                },
              },
            }} 
          />
        </IconButton>
      </Box>
    </Tooltip>
  );
}

export default MoaniPriceHeader;
